const BACK_PATH_KEY = '__experviserBackPath';

const DETAIL_MODULES = new Set([
  'contacts', 'companies', 'deals', 'leads', 'tasks', 'meetings',
  'activities', 'branches', 'brands', 'synergies', 'travel', 'files', 'notes',
]);

function localPath(value: unknown, origin: string): string | null {
  if (typeof value !== 'string' || !value || value.startsWith('//')) return null;
  try {
    const url = new URL(value, origin);
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

export type CrmBackTarget = { path: string; useBrowserHistory: boolean };

export function crmBackTarget(currentPath: string, state: unknown, referrer: string, origin: string): CrmBackTarget | null {
  const url = new URL(currentPath, origin);
  const [, moduleName, detailId] = url.pathname.split('/');
  const isEmailDetail = url.pathname === '/emails' && (url.searchParams.has('emailId') || url.searchParams.has('messageId'));
  const isRecordDetail = Boolean(detailId && DETAIL_MODULES.has(moduleName));
  const saved = state && typeof state === 'object'
    ? localPath((state as Record<string, unknown>)[BACK_PATH_KEY], origin)
    : null;
  if (saved && saved !== currentPath) return { path: saved, useBrowserHistory: true };

  // Full-page CRM links do not carry SPA history state. A same-origin referrer
  // still gives a safe, exact return destination; a direct deep link uses its
  // module list instead of leaving the CRM.
  const previous = isEmailDetail || isRecordDetail ? localPath(referrer, origin) : null;
  if (previous && previous !== currentPath) return { path: previous, useBrowserHistory: false };
  if (isEmailDetail) {
    url.searchParams.delete('emailId');
    url.searchParams.delete('messageId');
    return { path: `${url.pathname}${url.search}`, useBrowserHistory: false };
  }
  return isRecordDetail
    ? { path: `/${moduleName}`, useBrowserHistory: false }
    : null;
}

/** Attach the previous CRM URL to each SPA history entry. The browser owns
 * the stack; this marker only makes the visible mobile back control safe. */
export function installCrmBackTracking(onChange: () => void): () => void {
  const browserHistory = window.history;
  const originalPush = browserHistory.pushState;
  const originalReplace = browserHistory.replaceState;
  const currentPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const preserveBack = (state: unknown, path: unknown) => {
    const next = state && typeof state === 'object' ? { ...state } : {};
    if (!(BACK_PATH_KEY in next) && path) (next as Record<string, unknown>)[BACK_PATH_KEY] = path;
    return next;
  };
  browserHistory.pushState = function (state, title, url) {
    const previous = currentPath();
    originalPush.call(browserHistory, state, title, url);
    if (currentPath() !== previous) {
      originalReplace.call(browserHistory, {
        ...preserveBack(browserHistory.state, null), [BACK_PATH_KEY]: previous,
      }, '', window.location.href);
    }
    onChange();
  };
  browserHistory.replaceState = function (state, title, url) {
    const previous = browserHistory.state?.[BACK_PATH_KEY];
    originalReplace.call(browserHistory, preserveBack(state, previous), title, url);
    onChange();
  };
  window.addEventListener('popstate', onChange);
  return () => {
    browserHistory.pushState = originalPush;
    browserHistory.replaceState = originalReplace;
    window.removeEventListener('popstate', onChange);
  };
}
